import { Tooltip } from 'primereact/tooltip';
import { useEffect, useRef } from 'react';

interface TruncatedTextProps {
  /** The full text: drawn with an ellipsis where it does not fit, and the tooltip's content. */
  text: string;
  className?: string;
}

/** Whether the text is cut off -- the one-pixel slack absorbs sub-pixel rounding, as the master table's does. */
function isTruncated(element: HTMLElement | null): boolean {
  return element !== null && element.scrollWidth > element.clientWidth + 1;
}

/**
 * One line of text that ends in an ellipsis when it does not fit, and shows its full text in the application's
 * tooltip on hover -- only then: a name that fits needs no tooltip repeating it.
 *
 * <h2>The application's tooltip</h2>
 *
 * PrimeReact's `Tooltip`, styled once for the whole application in `index.css` ("Custom Tooltip styling"), as the
 * master tables use it. It is portalled to `<body>` above every overlay, so neither a dropdown's panel nor a
 * scrolling container clips it.
 *
 * <h2>Why the target is attached by hand</h2>
 *
 * The tooltip binds a `target` prop on a later re-render, not on mount. Text inside a dropdown's panel is mounted
 * when the panel opens and may never re-render before it is hovered, so the element is attached explicitly once
 * it exists, and detached when it goes.
 */
export function TruncatedText({ text, className }: TruncatedTextProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<Tooltip>(null);

  useEffect(() => {
    const element = textRef.current;
    const tooltip = tooltipRef.current;
    if (!element || !tooltip) return undefined;
    tooltip.updateTargetEvents(element);
    return () => {
      tooltip.unloadTargetEvents(element);
    };
  }, []);

  return (
    <>
      <span ref={textRef} className={className ? `truncated-text ${className}` : 'truncated-text'}>
        {text}
      </span>
      <Tooltip
        ref={tooltipRef}
        content={text}
        position="top"
        className="app-tooltip--topmost"
        onBeforeShow={() => isTruncated(textRef.current)}
      />
    </>
  );
}
